import { Injectable } from '@angular/core';
import { JSONPath } from 'jsonpath-plus';
import { Observable, debounceTime, finalize, tap } from 'rxjs';
import { CommonService } from 'src/app/shared/utility/common.service';
import { LongTermRentalState } from '../../long-term-rental/state/long-term-rental.state';
import { OrderApi } from '../../order/api/order.api';
import { OrderDetail, PropertyMap } from '../../order/interface/order.interface';
import { CostApproachState } from '../../order/state/cost-approach.state';
import { NeighborhoodState } from '../../order/state/neighborhood.state';
import { OrderState } from '../../order/state/order.state';
import { SalesApproachState } from '../../order/state/sales-approach.state';
import { PropertyState } from '../../property/state/property.state';
import { ScenarioState } from '../../scenario/state/scenario.state';
import { ShortTermRentalState } from '../../short-term-rental/state/short-term-rental.state';
import { SyncScope, SyncStatesWithoutUpdateFnArgs, SyncUpdateScope, SyncUpdateSet } from '../interface/sync.interface';
import { SyncState } from '../state/sync.state';

/** Marks the paths the backend stores as individual scoped adjustment entries. */
const UNIT_ADJUSTMENT_PATH_SEGMENT = '.adjustment.unitAdjustment';

@Injectable({
  providedIn: 'root',
})
export class SyncService {
  private readonly _unitPathRegex = /^structures\[(\d+)]\.units\[(\d+)]\.(.+)$/;
  private readonly _structurePathRegex = /^structures\[(\d+)]\.(.+)$/;

  constructor(
    private commonService: CommonService,
    private costApproachState: CostApproachState,
    private longTermRentalState: LongTermRentalState,
    private neighborhoodState: NeighborhoodState,
    private orderApi: OrderApi,
    private orderState: OrderState,
    private propertyState: PropertyState,
    private salesApproachState: SalesApproachState,
    private scenarioState: ScenarioState,
    private shortTermRentalState: ShortTermRentalState,
    private syncState: SyncState
  ) {
    this._syncOrderState();
    this._syncScenarioMapState();
    this._syncActiveScenarioWithScenarioMap();
    this._syncSubjectPropertyState();
    this._syncSalesApproachSelectedComparableListState();
    this._syncCostApproachSelectedComparableListState();
    this._syncShortTermRentSelectedComparableListState();
    this._syncLongTermRentSelectedComparableListState();
    this._syncNeighborhoodInsightState();
  }

  /**
   * This will update the states and their sync-states.
   * This will be used where states need to be updated without triggering /updateOrder API.
   */
  public syncLocally(...args: SyncStatesWithoutUpdateFnArgs) {
    const [scope, state] = args;

    if (scope == SyncScope.ORDER) {
      this.orderState.orderDetail = structuredClone(state as OrderDetail);
      this.syncState.syncedOrderDetail = structuredClone(state as OrderDetail);
    }

    if (scope == SyncScope.SCENARIO) {
      const scenarioMap = structuredClone(this.scenarioState.scenarioMap);

      if (state?.id && scenarioMap?.[state.id]) {
        scenarioMap[state.id] = state;
      }

      this.scenarioState.scenarioMap = structuredClone(scenarioMap);
      this.syncState.syncedScenarioMap = structuredClone(scenarioMap);
      this.scenarioState.activeScenario = state;
    }

    if (scope == SyncScope.SCENARIO_MAP) {
      this.scenarioState.scenarioMap = structuredClone(state);
      this.syncState.syncedScenarioMap = structuredClone(state);
    }

    if (scope == SyncScope.SUBJECT) {
      this.propertyState.subjectPropertyData = structuredClone(state);
      this.syncState.syncedSubjectPropertyData = structuredClone(state);
    }

    if (scope == SyncScope.SALES_SELECTED_COMPS) {
      this.salesApproachState.selectedComparableMap = structuredClone(state);
      this.syncState.syncedSelectedComparableMap = structuredClone(state);
    }

    if (scope == SyncScope.COST_SELECTED_COMPS) {
      this.costApproachState.selectedComparableMap = structuredClone(state);
      this.syncState.syncedCostApproachSelectedComparableMap = structuredClone(state);
    }

    if (scope == SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS) {
      this.shortTermRentalState.selectedComparableMap = structuredClone(state);
      this.syncState.syncedShortTermRentSelectedComparableMap = structuredClone(state);
    }

    if (scope == SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS) {
      this.longTermRentalState.selectedComparableMap = structuredClone(state);
      this.syncState.syncedLongTermRentSelectedComparableMap = structuredClone(state);
    }

    if (scope == SyncScope.NEIGHBORHOOD_INSIGHTS) {
      this.neighborhoodState.insight = structuredClone(state);
      this.syncState.syncedNeighborhoodInsight = structuredClone(state);
    }
  }

  /** A state change entered this scope's pipeline: busy, and one more revision to see through. */
  private _noteChange(scope: SyncScope): void {
    this.syncState.setIsSyncInProgress(scope, true);
    this.syncState.noteSyncRequested(scope);
  }

  /** The debounce fired with nothing to send, which settles everything requested so far. */
  private _settleIdle(scope: SyncScope): void {
    this.syncState.setIsSyncInProgress(scope, false);
    this.syncState.settleWithoutRequest(scope);
  }

  /** Sends one scope's update and tracks it until it finishes, whether it succeeds or fails. */
  private _send(
    scope: SyncScope,
    orderId: string,
    reportType: string,
    updateSet: SyncUpdateSet[]
  ): Observable<boolean> {
    const batch = this.syncState.startSyncBatch(scope);

    return this.orderApi.updateOrder(orderId, reportType, updateSet).pipe(
      finalize(() => {
        this.syncState.setIsSyncInProgress(scope, false);
        this.syncState.finishSyncBatch(scope, batch);
      })
    );
  }

  private _syncOrderState() {
    this.orderState.orderDetail$
      .pipe(
        tap(() => this._noteChange(SyncScope.ORDER)),
        debounceTime(2000)
      )
      .subscribe((res: OrderDetail) => {
        const newOrder = structuredClone(res);
        const syncedOrder = structuredClone(this.syncState.syncedOrderDetail);

        const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
          syncedOrder,
          newOrder
        );

        if (!modifiedJsonPathList?.length) {
          this._settleIdle(SyncScope.ORDER);
          return;
        }

        const updateSet: SyncUpdateSet[] = [];

        modifiedJsonPathList.forEach((modifiedPath) => {
          if (modifiedPath.includes('primarySearchFilter')) return;

          const modifiedValue = JSONPath({ json: newOrder, path: modifiedPath, wrap: false });
          updateSet.push({
            scope: SyncUpdateScope.ORDER,
            jsonPath: modifiedPath,
            value: modifiedValue,
          });
        });

        if (!updateSet.length) {
          this._settleIdle(SyncScope.ORDER);
          return;
        }

        this._send(SyncScope.ORDER, newOrder.id, newOrder.reportType, updateSet).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedOrderDetail = newOrder;
        });
      });
  }

  private _syncScenarioMapState() {
    this.scenarioState.scenarioMap$
      .pipe(
        tap(() => this._noteChange(SyncScope.SCENARIO_MAP)),
        debounceTime(2000)
      )
      .subscribe((res) => {
        if (!res || !this.syncState.syncedScenarioMap) {
          this._settleIdle(SyncScope.SCENARIO_MAP);
          return;
        }

        const newScenarioMap = structuredClone(res);
        const newScenarioList = Object.values(newScenarioMap);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedScenarioList = structuredClone(Object.values(this.syncState.syncedScenarioMap));
        const updateSet: SyncUpdateSet[] = [];

        for (let i = 0; i < newScenarioList.length; i++) {
          const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
            syncedScenarioList[i],
            newScenarioList[i]
          );

          if (!modifiedJsonPathList?.length) continue;

          modifiedJsonPathList.forEach((modifiedPath) => {
            const modifiedValue = JSONPath({ json: newScenarioList[i], path: modifiedPath, wrap: false });

            this._toUnitAdjustmentLeaves(modifiedPath, modifiedValue).forEach(({ jsonPath, value }) => {
              updateSet.push({
                id: newScenarioList[i].id,
                scope: SyncUpdateScope.SCENARIO,
                jsonPath,
                value,
              });
            });
          });
        }

        if (!updateSet.length) {
          this._settleIdle(SyncScope.SCENARIO_MAP);
          return;
        }

        this._send(SyncScope.SCENARIO_MAP, order.id, order.reportType, updateSet).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedScenarioMap = newScenarioMap;
        });
      });
  }

  /**
   * Splits a unit adjustment update into one path per field. The backend keeps these as individual scoped
   * entries and rejects any path that does not name a single numeric field, while the differ reports a whole
   * array whenever its length changes - which it does as soon as a unit gains its first adjustment.
   */
  private _toUnitAdjustmentLeaves(jsonPath: string, value: unknown): Array<{ jsonPath: string; value: unknown }> {
    if (!jsonPath.includes(UNIT_ADJUSTMENT_PATH_SEGMENT)) return [{ jsonPath, value }];

    if (value == null || typeof value !== 'object') return [{ jsonPath, value }];

    if (Array.isArray(value)) {
      // A gap in the list is a structure or unit that carries no adjustment, so it has nothing to send.
      return value.flatMap((entry, index) =>
        entry == null ? [] : this._toUnitAdjustmentLeaves(`${jsonPath}[${index}]`, entry)
      );
    }

    return Object.entries(value).flatMap(([key, child]) => this._toUnitAdjustmentLeaves(`${jsonPath}.${key}`, child));
  }

  private _syncActiveScenarioWithScenarioMap(): void {
    this.scenarioState.activeScenario$.subscribe((res) => {
      if (!res) return;

      const scenarioMap = structuredClone(this.scenarioState.scenarioMap);
      this.scenarioState.scenarioMap = {
        ...scenarioMap,
        [res.id]: res,
      };
    });
  }

  /**
   * Builds a scoped SyncUpdateSet entry by detecting STRUCTURE/UNIT patterns in the modified path.
   * Strips the structures[n]/units[m] prefix from the jsonPath so the backend receives just the field path.
   */
  private _buildScopedUpdateSet(propertyId: string, modifiedPath: string, modifiedValue: unknown): SyncUpdateSet {
    const unitMatch = modifiedPath.match(this._unitPathRegex);
    if (unitMatch) {
      return {
        id: propertyId,
        scope: SyncUpdateScope.UNIT,
        structure: parseInt(unitMatch[1]),
        unit: parseInt(unitMatch[2]),
        jsonPath: unitMatch[3],
        value: modifiedValue,
      };
    }

    const structureMatch = modifiedPath.match(this._structurePathRegex);
    if (structureMatch) {
      return {
        id: propertyId,
        scope: SyncUpdateScope.STRUCTURE,
        structure: parseInt(structureMatch[1]),
        jsonPath: structureMatch[2],
        value: modifiedValue,
      };
    }

    return {
      id: propertyId,
      scope: SyncUpdateScope.PROPERTY,
      jsonPath: modifiedPath,
      value: modifiedValue,
    };
  }

  private _syncSubjectPropertyState() {
    // TODO: Merge all Property state
    this.propertyState.subjectPropertyData$
      .pipe(
        tap(() => this._noteChange(SyncScope.SUBJECT)),
        debounceTime(2000)
      )
      .subscribe((res) => {
        if (!res) {
          this._settleIdle(SyncScope.SUBJECT);
          return;
        }

        const newProperty = structuredClone(res);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedProperty = structuredClone(this.syncState.syncedSubjectPropertyData);

        const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
          syncedProperty,
          newProperty
        );

        if (!modifiedJsonPathList?.length) {
          this._settleIdle(SyncScope.SUBJECT);
          return;
        }

        const updateSet: SyncUpdateSet[] = [];

        modifiedJsonPathList.forEach((modifiedPath) => {
          const modifiedValue = JSONPath({ json: newProperty, path: modifiedPath, wrap: false });
          updateSet.push(this._buildScopedUpdateSet(newProperty.id!, modifiedPath, modifiedValue));
        });

        this._send(SyncScope.SUBJECT, order.id, order.reportType, updateSet).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedSubjectPropertyData = newProperty;
        });
      });
  }

  private _syncSalesApproachSelectedComparableListState() {
    this.salesApproachState.selectedComparableMap$
      .pipe(
        tap(() => this._noteChange(SyncScope.SALES_SELECTED_COMPS)),
        debounceTime(2000)
      )
      .subscribe((res: PropertyMap) => {
        const newComparableMap = structuredClone(res);
        const newComparableList = Object.values(newComparableMap);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedComparableList = structuredClone(Object.values(this.syncState.syncedSelectedComparableMap));
        const updateSet: SyncUpdateSet[] = [];

        for (let i = 0; i < newComparableList.length; i++) {
          const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
            syncedComparableList[i],
            newComparableList[i]
          );

          if (!modifiedJsonPathList?.length) continue;

          modifiedJsonPathList.forEach((modifiedPath) => {
            const modifiedValue = JSONPath({ json: newComparableList[i], path: modifiedPath, wrap: false });
            updateSet.push(this._buildScopedUpdateSet(newComparableList[i].id!, modifiedPath, modifiedValue));
          });
        }

        if (!updateSet.length) {
          this._settleIdle(SyncScope.SALES_SELECTED_COMPS);
          return;
        }

        this._send(SyncScope.SALES_SELECTED_COMPS, order.id, order.reportType, updateSet).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedSelectedComparableMap = newComparableMap;
        });
      });
  }

  private _syncNeighborhoodInsightState() {
    this.neighborhoodState.insight$
      .pipe(
        tap(() => this._noteChange(SyncScope.NEIGHBORHOOD_INSIGHTS)),
        debounceTime(2000)
      )
      .subscribe((res) => {
        if (!res || !this.syncState.syncedNeighborhoodInsight) {
          this._settleIdle(SyncScope.NEIGHBORHOOD_INSIGHTS);
          return;
        }

        const newInsight = structuredClone(res);
        const syncedInsight = structuredClone(this.syncState.syncedNeighborhoodInsight);

        const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
          syncedInsight,
          newInsight
        );

        if (!modifiedJsonPathList?.length) {
          this._settleIdle(SyncScope.NEIGHBORHOOD_INSIGHTS);
          return;
        }

        const updateSet: SyncUpdateSet[] = [];

        modifiedJsonPathList.forEach((modifiedPath) => {
          const modifiedValue = JSONPath({ json: newInsight, path: modifiedPath, wrap: false });
          updateSet.push({
            id: this.scenarioState.activeScenario!.id,
            scope: SyncUpdateScope.SCENARIO,
            jsonPath: 'neighborhood.' + modifiedPath,
            value: modifiedValue,
          });
        });

        this._send(
          SyncScope.NEIGHBORHOOD_INSIGHTS,
          this.orderState.orderDetail.id,
          this.orderState.orderDetail.reportType,
          updateSet
        ).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedNeighborhoodInsight = newInsight;
        });
      });
  }

  private _syncCostApproachSelectedComparableListState() {
    this.costApproachState.selectedComparableMap$
      .pipe(
        tap(() => this._noteChange(SyncScope.COST_SELECTED_COMPS)),
        debounceTime(2000)
      )
      .subscribe((res: PropertyMap) => {
        const newComparableMap = structuredClone(res);
        const newComparableList = Object.values(newComparableMap);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedComparableMap = structuredClone(this.syncState.syncedCostApproachSelectedComparableMap);
        const syncedComparableList = Object.values(syncedComparableMap);
        const updateSet: SyncUpdateSet[] = [];

        for (let i = 0; i < newComparableList.length; i++) {
          const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
            syncedComparableList[i],
            newComparableList[i]
          );

          if (!modifiedJsonPathList?.length) continue;

          modifiedJsonPathList.forEach((modifiedPath) => {
            const modifiedValue = JSONPath({ json: newComparableList[i], path: modifiedPath, wrap: false });
            updateSet.push(this._buildScopedUpdateSet(newComparableList[i].id!, modifiedPath, modifiedValue));
          });
        }

        if (!updateSet.length) {
          this._settleIdle(SyncScope.COST_SELECTED_COMPS);
          return;
        }

        this._send(SyncScope.COST_SELECTED_COMPS, order.id, order.reportType, updateSet).subscribe((success) => {
          if (!success) return;

          this.syncState.syncedCostApproachSelectedComparableMap = newComparableMap;
        });
      });
  }

  private _syncShortTermRentSelectedComparableListState() {
    this.shortTermRentalState.selectedComparableMap$
      .pipe(
        tap(() => this._noteChange(SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS)),
        debounceTime(2000)
      )
      .subscribe((res: PropertyMap) => {
        const newComparableMap = structuredClone(res);
        const newComparableList = Object.values(newComparableMap);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedComparableMap = structuredClone(this.syncState.syncedShortTermRentSelectedComparableMap);
        const syncedComparableList = Object.values(syncedComparableMap);
        const updateSet: SyncUpdateSet[] = [];

        for (let i = 0; i < newComparableList.length; i++) {
          const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
            syncedComparableList[i],
            newComparableList[i]
          );

          if (!modifiedJsonPathList?.length) continue;

          modifiedJsonPathList.forEach((modifiedPath) => {
            const modifiedValue = JSONPath({ json: newComparableList[i], path: modifiedPath, wrap: false });
            updateSet.push(this._buildScopedUpdateSet(newComparableList[i].id!, modifiedPath, modifiedValue));
          });
        }

        if (!updateSet.length) {
          this._settleIdle(SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS);
          return;
        }

        this._send(SyncScope.SHORT_TERM_RENTAL_SELECTED_COMPS, order.id, order.reportType, updateSet).subscribe(
          (success) => {
            if (!success) return;

            this.syncState.syncedShortTermRentSelectedComparableMap = newComparableMap;
          }
        );
      });
  }

  private _syncLongTermRentSelectedComparableListState() {
    this.longTermRentalState.selectedComparableMap$
      .pipe(
        tap(() => this._noteChange(SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS)),
        debounceTime(2000)
      )
      .subscribe((res: PropertyMap) => {
        const newComparableMap = structuredClone(res);
        const newComparableList = Object.values(newComparableMap);
        const order = structuredClone(this.orderState.orderDetail);
        const syncedComparableMap = structuredClone(this.syncState.syncedLongTermRentSelectedComparableMap);
        const syncedComparableList = Object.values(syncedComparableMap);
        const updateSet: SyncUpdateSet[] = [];

        for (let i = 0; i < newComparableList.length; i++) {
          const modifiedJsonPathList: string[] | undefined = this.commonService.getModifiedJsonPaths(
            syncedComparableList[i],
            newComparableList[i]
          );

          if (!modifiedJsonPathList?.length) continue;

          modifiedJsonPathList.forEach((modifiedPath) => {
            const modifiedValue = JSONPath({ json: newComparableList[i], path: modifiedPath, wrap: false });
            updateSet.push(this._buildScopedUpdateSet(newComparableList[i].id!, modifiedPath, modifiedValue));
          });
        }

        if (!updateSet.length) {
          this._settleIdle(SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS);
          return;
        }

        this._send(SyncScope.LONG_TERM_RENTAL_SELECTED_COMPS, order.id, order.reportType, updateSet).subscribe(
          (success) => {
            if (!success) return;

            this.syncState.syncedLongTermRentSelectedComparableMap = newComparableMap;
          }
        );
      });
  }
}
